import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import styles from "./DiamondListItem.module.css";
import { ReactComponent as Checkmark } from "../../assets/checkmark.svg";
import AccessLevel from "ManagementTeam/DashboardPage/AccessLevel";
import { Select } from "shared/Select/Select";
import { ReactComponent as AlertIcon } from "../../assets/Alert_Icon.svg";

/**
 * Interactive component. Displays information about the committee member.
 * Can be edited.
 */

export function DiamondListItem({ diamond, edit, onChange, onNameClick, isWait }) {
  return (
    <div className={styles.container}>
      <label htmlFor="name" hidden>
        Diamond
      </label>
      <input
        type="text"
        name="name"
        value={diamond.name}
        className={styles.input}
        readOnly
        disabled={edit}
        onClick={onNameClick}
        style={{ cursor: isWait ? "wait" : !edit ? "pointer" : "default" }}
      />
      <label htmlFor="gar-points" hidden>
        Gar Points
      </label>
      <input
        className={classNames(styles.center, styles.input)}
        type="number"
        step="any"
        name="gar-points"
        value={diamond.garPoints}
        onChange={(key) => {
          if (key.target.value.length < 1) {
            key.target.value = 0;
          }
          if (key.target.value.match(/^\d*\.?\d*$/)) {
            if (
              key.target.value.length > 1 &&
              parseInt(key.target.value[0]) === 0 &&
              key.target.value !== "."
            ) {
              onChange(key.target.value.substring(1), "garPoints");
            } else {
              onChange(key.target.value, "garPoints");
            }
          }
        }}
        disabled={!edit}
      />
      <label htmlFor="integrated-gar" hidden>
        Integrated Gar
      </label>
      <input
        className={classNames(styles.center, styles.input)}
        type="number"
        step="any"
        name="integrated-gar"
        value={diamond.integratedGar}
        onChange={(key) => {
          if (key.target.value.length < 1) {
            key.target.value = 0;
          }
          if (key.target.value.match(/^\d*\.?\d*$/)) {
            if (
              key.target.value.length > 1 &&
              parseInt(key.target.value[0]) === 0 &&
              key.target.value !== "."
            ) {
              onChange(key.target.value.substring(1), "integratedGarPoints");
            } else {
              onChange(key.target.value, "integratedGarPoints");
            }
          }
        }}
        disabled={!edit}
      />
      <label htmlFor="pin-wearing" hidden>
        Pin Wearing
      </label>
      <input
        type="text"
        name="pin-wearing"
        value={diamond.pinWearing}
        onChange={(key) => onChange(key.target.value, "pinWearing")}
        disabled={!edit}
        className={styles.input}
      />
      <label htmlFor="executive-committee" hidden>
        Executive Committee
      </label>
      <label htmlFor="voting-member" hidden />
      <div className={styles["center-label"]}>
        <label>
          <input
            type="checkbox"
            name="executive-committee"
            checked={diamond.executiveCommittee}
            disabled={!edit}
            hidden
            className={classNames(styles.input)}
          />
          <span
            className={classNames(
              styles["checkmark-img"],
              {
                [styles.invisible]: !diamond.executiveCommittee,
              },
              edit ? styles.checkBox : ""
            )}
            onClick={() =>
              edit &&
              onChange(!diamond.executiveCommittee, "isExecutiveCommittee")
            }
          >
            <Checkmark />
          </span>
        </label>
      </div>
      <label htmlFor="voting-member" hidden>
        Voting Member
      </label>
      <div className={styles["center-label"]}>
        <label>
          <input
            type="checkbox"
            name="voting-member"
            checked={diamond.votingMember}
            disabled
            hidden
            className={styles.input}
          />
          <span
            className={classNames(styles["checkmark-img"], {
              [styles.invisible]: !diamond.votingMember,
            })}
          >
            <Checkmark />
          </span>
        </label>
      </div>
      <label htmlFor="capital-share" hidden>
        Capital Share
      </label>
      <input
        className={classNames(styles.center, styles.input)}
        type="number"
        step="any"
        name="capital-share"
        value={diamond.capitalShare}
        onChange={(key) => {
          if (key.target.value.length < 1) {
            key.target.value = 0;
          }
          if (key.target.value.match(/^\d*\.?\d*$/)) {
            if (
              key.target.value.length > 1 &&
              parseInt(key.target.value[0]) === 0 &&
              key.target.value !== "."
            ) {
              onChange(key.target.value.substring(1), "capitalShare");
            } else {
              onChange(key.target.value, "capitalShare");
            }
          }
        }}
        disabled={!edit}
      />
      <label htmlFor="year-qualified" hidden>
        Year Qualified
      </label>
      <input
        className={classNames(styles.center, styles.input)}
        type="number"
        name="year-qualified"
        value={diamond.yearQualified}
        onChange={(key) => {
          if (key.target.value.length < 1) {
            key.target.value = 0;
          }
          if (key.target.value.match(/[0-9]+/)) {
            if (key.target.value.length > 1 && key.target.value[0] === "0") {
              onChange(
                parseInt(key.target.value.substring(1)),
                "yearDiamondQualified"
              );
            } else
              onChange(parseInt(key.target.value), "yearDiamondQualified");
          }
        }}
        disabled={!edit}
      />
      <label htmlFor="access-level" hidden>
        MT Group
      </label>
      {!edit ? (
        <div className={styles.accessLevel}>
          <AccessLevel
            accessLevel={diamond.accessLevel}
            textColor={"#ffffff"}
          />
        </div>
      ) : (
        <span className={styles.selectContainer}>
          <Select
            placeholder={diamond.accessLevel}
            selectedOption={diamond.accessLevel}
            options={["MT1", "MT2", "GUEST", "NONE"]}
            onChange={(e) => {
              onChange(e, "overrideAccessLevel");
            }}
            disabled={!edit}
          ></Select>
        </span>
      )}

      {diamond.actualAccessLevel != diamond.accessLevel ? (
        <span className={!edit ? styles.alertContainer : styles.alertEdit}>
          <AlertIcon></AlertIcon>
        </span>
      ) : null}
    </div>
  );
}

DiamondListItem.propTypes = {
  /**
   * Object with the Diamond's info.
   */
  diamond: PropTypes.shape({
    name: PropTypes.string,
    garPoints: PropTypes.number,
    integratedGar: PropTypes.number,
    pinWearing: PropTypes.string,
    executiveCommittee: PropTypes.bool,
    votingMember: PropTypes.bool,
    capitalShare: PropTypes.number,
    yearQualified: PropTypes.string,
  }),
  /**
   * Boolean that determines if the text boxes are editable.
   */
  edit: PropTypes.bool,
  /**
   * Function that fires when the diamond's info is updated.
   */
  onChange: PropTypes.func,
};
